import type { FC, ReactNode } from 'react'
import type { MotionValue } from 'motion/react'

import { useId } from 'react'
import { motion } from 'motion/react'
import { cn } from '@/lib/utils'

import {
    BODY_PATH,
    BODY_STOPS,
    CAP_PATH,
    CAP_STOPS,
    CRIMP_STOPS,
    DROP_PATH,
    LABEL,
    TUBE_HEIGHT,
    TUBE_WIDTH
} from '@/lib/tube-geometry'

interface TubeRenderProps {
    className?: string
    // Accessible name. Leave empty when the tube is decoration next to a caption.
    label?: string
    // Slides the glossy highlight sideways, so a tilt reads as real volume.
    highlightShift?: MotionValue<number>
    // Small print on the label, only worth drawing when the tube is large.
    details?: boolean
}

const TubeRender: FC<TubeRenderProps> = ({ className, label, highlightShift, details = true }): ReactNode => {
    const uid = useId().replace(/[^a-zA-Z0-9]/g, '')
    const id = (name: string): string => `tube-${uid}-${name}`
    const url = (name: string): string => `url(#${id(name)})`

    return (
        <svg
            viewBox={`0 0 ${TUBE_WIDTH} ${TUBE_HEIGHT}`}
            className={cn('block size-full overflow-visible', className)}
            role={label ? 'img' : undefined}
            aria-label={label}
            aria-hidden={label ? undefined : true}
            focusable='false'
        >
            <defs>
                <linearGradient id={id('body')} x1='34' y1='0' x2='266' y2='0' gradientUnits='userSpaceOnUse'>
                    {BODY_STOPS.map(([offset, color]) => (
                        <stop key={offset} offset={offset} stopColor={color} />
                    ))}
                </linearGradient>

                <linearGradient id={id('shade')} x1='0' y1='54' x2='0' y2='538' gradientUnits='userSpaceOnUse'>
                    <stop offset='0' stopColor='#FFFFFF' stopOpacity='0.16' />
                    <stop offset='0.12' stopColor='#FFFFFF' stopOpacity='0' />
                    <stop offset='0.72' stopColor='#000B40' stopOpacity='0' />
                    <stop offset='1' stopColor='#000B40' stopOpacity='0.32' />
                </linearGradient>

                <linearGradient id={id('crimp')} x1='30' y1='0' x2='270' y2='0' gradientUnits='userSpaceOnUse'>
                    {CRIMP_STOPS.map(([offset, color]) => (
                        <stop key={offset} offset={offset} stopColor={color} />
                    ))}
                </linearGradient>

                <linearGradient id={id('crimpFold')} x1='0' y1='16' x2='0' y2='58' gradientUnits='userSpaceOnUse'>
                    <stop offset='0' stopColor='#FFFFFF' stopOpacity='0.22' />
                    <stop offset='0.35' stopColor='#FFFFFF' stopOpacity='0' />
                    <stop offset='0.8' stopColor='#000B40' stopOpacity='0.1' />
                    <stop offset='1' stopColor='#000B40' stopOpacity='0.3' />
                </linearGradient>

                <pattern id={id('ridges')} x='30' y='0' width='5' height='80' patternUnits='userSpaceOnUse'>
                    <rect width='1.6' height='80' fill='#081A6E' fillOpacity='0.3' />
                    <rect x='2.6' width='0.9' height='80' fill='#FFFFFF' fillOpacity='0.14' />
                </pattern>

                <linearGradient id={id('cap')} x1='78' y1='0' x2='222' y2='0' gradientUnits='userSpaceOnUse'>
                    {CAP_STOPS.map(([offset, color]) => (
                        <stop key={offset} offset={offset} stopColor={color} />
                    ))}
                </linearGradient>

                <linearGradient id={id('capShade')} x1='0' y1='530' x2='0' y2='664' gradientUnits='userSpaceOnUse'>
                    <stop offset='0' stopColor='#4A2B20' stopOpacity='0.22' />
                    <stop offset='0.18' stopColor='#4A2B20' stopOpacity='0' />
                    <stop offset='0.85' stopColor='#4A2B20' stopOpacity='0' />
                    <stop offset='1' stopColor='#4A2B20' stopOpacity='0.16' />
                </linearGradient>

                <pattern id={id('capRidges')} x='78' y='0' width='6' height='160' patternUnits='userSpaceOnUse'>
                    <rect width='1' height='160' fill='#4A2B20' fillOpacity='0.07' />
                </pattern>

                <linearGradient id={id('gloss')} x1='0' y1='60' x2='0' y2='480' gradientUnits='userSpaceOnUse'>
                    <stop offset='0' stopColor='#FFFFFF' stopOpacity='0' />
                    <stop offset='0.1' stopColor='#FFFFFF' stopOpacity='0.92' />
                    <stop offset='0.55' stopColor='#FFFFFF' stopOpacity='0.7' />
                    <stop offset='0.88' stopColor='#FFFFFF' stopOpacity='0.3' />
                    <stop offset='1' stopColor='#FFFFFF' stopOpacity='0' />
                </linearGradient>

                <filter id={id('blurSm')} x='-50%' y='-10%' width='200%' height='120%'>
                    <feGaussianBlur stdDeviation='2.2' />
                </filter>

                <filter id={id('blurLg')} x='-80%' y='-20%' width='260%' height='140%'>
                    <feGaussianBlur stdDeviation='9' />
                </filter>

                <clipPath id={id('bodyClip')}>
                    <path d={BODY_PATH} />
                </clipPath>
            </defs>

            {/* Cap, drawn first so the shoulders of the tube sit over its top edge. */}
            <path d={CAP_PATH} fill={url('cap')} />
            <path d={CAP_PATH} fill={url('capRidges')} />
            <path d={CAP_PATH} fill={url('capShade')} />
            <rect x='78' y='552' width='144' height='1.6' fill='#FFFFFF' fillOpacity='0.85' />
            <rect x='78' y='554' width='144' height='1' fill='#4A2B20' fillOpacity='0.1' />
            <rect x='99' y='560' width='9' height='96' rx='4.5' fill='#FFFFFF' fillOpacity='0.95' filter={url('blurSm')} />
            <rect x='196' y='562' width='6' height='90' rx='3' fill='#FFFFFF' fillOpacity='0.45' filter={url('blurSm')} />
            <path d='M80 663 H220' stroke='#4A2B20' strokeOpacity='0.12' strokeWidth='1.2' />
            <ellipse cx='150' cy='540' rx='76' ry='9' fill='#0B1033' fillOpacity='0.22' filter={url('blurSm')} />

            {/* Body */}
            <path d={BODY_PATH} fill={url('body')} />
            <path d={BODY_PATH} fill={url('shade')} />

            {/* Highlights live inside the body and slide with the tilt. */}
            <g clipPath={url('bodyClip')}>
                <motion.g style={highlightShift ? { x: highlightShift } : undefined}>
                    <path d='M56 64 L116 64 L118 476 L64 476 Z' fill='#FFFFFF' fillOpacity='0.13' filter={url('blurLg')} />
                    <path d='M81 70 L92 70 L96 474 L87 474 Z' fill={url('gloss')} filter={url('blurSm')} />
                    <path d='M247 68 L255 68 L246 468 L239 468 Z' fill='#FFFFFF' fillOpacity='0.2' filter={url('blurSm')} />
                </motion.g>

                <path d='M50 474 Q58 516 98 534' fill='none' stroke='#FFFFFF' strokeOpacity='0.32' strokeWidth='4' strokeLinecap='round' filter={url('blurSm')} />
                <path d='M34 60 H266' stroke='#FFFFFF' strokeOpacity='0.25' strokeWidth='2' filter={url('blurSm')} />
            </g>

            {/* Crimped seal */}
            <rect x='30' y='16' width='240' height='42' rx='5' fill={url('crimp')} />
            <rect x='30' y='16' width='240' height='42' rx='5' fill={url('ridges')} />
            <rect x='30' y='16' width='240' height='42' rx='5' fill={url('crimpFold')} />
            <rect x='36' y='18.5' width='228' height='1.6' rx='0.8' fill='#FFFFFF' fillOpacity='0.4' />
            <rect x='34' y='57' width='232' height='7' fill='#000B40' fillOpacity='0.16' filter={url('blurSm')} />

            {/* Label */}
            <g fill='#FFFFFF'>
                <path d={DROP_PATH} transform='translate(141 166) scale(0.9)' fillOpacity='0.95' />

                <text
                    x={LABEL.x}
                    y={LABEL.y}
                    textAnchor='middle'
                    fontSize={LABEL.size}
                    letterSpacing='1.5'
                    stroke='#FFFFFF'
                    strokeWidth={LABEL.weight}
                    strokeLinejoin='round'
                    strokeLinecap='round'
                    style={{ fontFamily: 'var(--font-app-heading, cursive)' }}
                >
                    {LABEL.text}
                </text>

                <text
                    x='151.6'
                    y='352'
                    textAnchor='middle'
                    fontSize='13'
                    fontWeight='600'
                    letterSpacing='3'
                    style={{ fontFamily: 'var(--font-app-sans, ui-sans-serif, system-ui, sans-serif)' }}
                >
                    ALL-OVER BALM
                </text>

                {details && (
                    <>
                        <rect x='134' y='370' width='32' height='1.6' />

                        <text
                            x='151.1'
                            y='436'
                            textAnchor='middle'
                            fontSize='10'
                            fontWeight='600'
                            letterSpacing='2.2'
                            style={{ fontFamily: 'var(--font-app-sans, ui-sans-serif, system-ui, sans-serif)' }}
                        >
                            FRAGRANCE-FREE
                        </text>
                    </>
                )}
            </g>
        </svg>
    )
}

export default TubeRender
